import { useCallback } from 'react';
import {
  type BrandWithSupport,
  useDeleteBrandWithRelatedData,
} from 'src/graphql/entities/brand';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';

interface StartDeleteParams {
  segment: BrandWithSupport;
  sitesCount: number;
}

const buildBlockedBody = (segmentName: string, sitesCount: number): string => {
  const siteWord = sitesCount === 1 ? 'site' : 'sites';
  if (!sitesCount) {
    return `${segmentName} is in use by one or more sites. Reassign each site to another segment or delete the sites first, then try again.`;
  }
  return `This segment is in use by ${sitesCount} ${siteWord}. Reassign each site to another segment or delete the sites first, then try again.`;
};

export const useClientSegmentDeleteFlow = () => {
  const { confirm, alert } = useConfirmationModal();
  const { showSuccessAlert } = useNotifications();
  const { deleteBrand } = useDeleteBrandWithRelatedData();

  const start = useCallback(
    async ({ segment, sitesCount }: StartDeleteParams) => {
      const segmentName = segment.name ?? 'this segment';

      const proceed = await confirm({
        title: 'Delete segment?',
        body: `${segmentName} is removed permanently, and its conversations, billing references and assignments lose their brand.`,
        confirmLabel: 'Delete segment',
        danger: true,
      });
      if (!proceed) return;

      if (segment.partnerId === null || segment.partnerId === undefined) {
        await alert({
          title: "Can't delete segment",
          body: `The partner of ${segmentName} is unknown.`,
          acknowledgeLabel: 'Got it',
        });
        return;
      }

      const result = await deleteBrand({
        brandId: segment.brandId,
        partnerId: segment.partnerId,
      });

      if (result.success) {
        showSuccessAlert('Client segment deleted.');
        return;
      }

      await alert({
        title: "Can't delete segment",
        body: buildBlockedBody(segmentName, sitesCount),
        acknowledgeLabel: 'Got it',
      });
    },
    [alert, confirm, deleteBrand, showSuccessAlert],
  );

  return { start };
};
